import React, { FC, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Typography, Box, Divider, Grid } from '@mui/material';
import MediaUploader from './MediaUploader';
import UploadPreviewCard from './UploadPreviewCard';
import MediaGallery from './MediaGallery';
import { ProjectMedia } from 'src/api/Api';
import { useAppState } from 'src/state-management/state-provider';

const ProjectMediaPage: FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const { dispatch } = useAppState();
  const [filesToUpload, setFilesToUpload] = useState<File[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<string[]>([]);

  const handleFilesAccepted = (files: File[]) => {
    const newFiles = files.filter(file =>
      !filesToUpload.some(existingFile => existingFile.name === file.name) &&
      !uploadedFiles.includes(file.name)
    );
    setFilesToUpload((prevFiles) => [...prevFiles, ...newFiles]);
  };

  const handleRemoveFile = useCallback((fileToRemove: File) => {
    setFilesToUpload((prevFiles) => prevFiles.filter(file => file !== fileToRemove));
  }, []);

  const handleUploadComplete = useCallback((fileName: string) => {
    setUploadedFiles(prev => [...prev, fileName]);
  }, []);

  const handleFinalize = useCallback((mediaItem: ProjectMedia) => {
    dispatch({
      type: 'ADMIN_ADD_PROJECT_MEDIA',
      payload: { projectId: mediaItem.project_id, mediaItem }
    });
    // Remove the file from the upload list
    setFilesToUpload(prevFiles => prevFiles.filter(f => f.name !== mediaItem.file_name));
  }, [dispatch]);

  if (!projectId) {
    return <Typography>Project ID is missing.</Typography>;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Manage Project Media
      </Typography>

      <MediaUploader onFilesAccepted={handleFilesAccepted} />

      <Box sx={{ mt: 4 }}>
        <Typography variant="h6">Uploads</Typography>
        {filesToUpload.length > 0 ? (
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {filesToUpload.map((file, index) => (
              <Grid item key={index} xs={12} sm={6} md={4} lg={3}>
                <UploadPreviewCard
                  file={file}
                  projectId={projectId}
                  onRemove={handleRemoveFile}
                  onUploadComplete={handleUploadComplete}
                  onFinalize={handleFinalize}
                />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Typography sx={{ mt: 2, color: 'text.secondary' }}>No files selected for upload.</Typography>
        )}
      </Box>

      <Divider sx={{ my: 4 }} />

      <Box>
        <Typography variant="h6">Uploaded Media</Typography>
        <MediaGallery projectId={projectId} />
      </Box>
    </Box>
  );
};

export default ProjectMediaPage;
